import app from 'app';
import Vue from 'vue';
import { workPool } from '@wochitnpm/helpers';

/**
 * trim video fragment using `startTime`, `endTime`
 *
 * the result is:
 * 1) updated thumbnail showing the start frame
 * 2) actual trimmed video content
 * 3) updated sprite image
 *
 * generate `previewResultData` for video-fragment asset
 * trimmed from `startTime` till `endTime`
 */

/** @namespace workPool.job.trimVideoFragment */
export default workPool.define(
  'trimVideoFragment',
  {
    getContext() {
      const asset = this.options.asset;
      return {
        // asset id already contains startTime and endTime
        assetId: asset.id,
      };
    },

    initialize() {
      this.etWatchdog = new app.namespace.Model.TimedExecutor({
        timeout: [2],
        run: this.fnWatchdog.bind(this),
      });
    },

    run() {
      const asset = this.options.asset;
      this.beforeRun();
      const start = asset.startTime;
      const end = asset.endTime;
      if (start === 0 && end === asset.metadata.duration) {
        this.onDone({
          previewResultData: {
            startTime: 0,
            endTime: end,
            cdnUrl: asset.metadata.urlCdn,
            videoAssetStoryBoard: asset.metadata.videoAssetStoryBoard,
            thumbnail:
              asset.thumbnail ||
              asset.metadata.thumbnail256x144Url ||
              asset.metadata.thumbnail160x90Url ||
              null,
          },
        });
        return;
      }
      app.api.videoTrimming
        .post({
          url: asset.metadata.urlCdn,
          start,
          end,
        })
        .then(
          () => {
            this.etWatchdog.start();
          },
          () => {
            this.onFail();
          }
        );
    },

    fnWatchdog(loop) {
      const asset = this.options.asset;
      app.api.videoTrimming
        .get({
          url: asset.metadata.urlCdn,
          start: asset.startTime,
          end: asset.endTime,
        })
        .then(
          (res) => {
            this.onDone(res);
          },
          (res) => {
            if (res?.errorCode === 401) {
              loop.reject();
            } else {
              loop.resolve();
            }
          }
        );
    },

    beforeRun() {
      const asset = this.options.asset;
      Vue.set(asset, '_previewOp', 'get');
    },

    onDone(res) {
      const asset = this.options.asset;
      app.prependHttpAcrossObject(res);

      Vue.set(asset, 'previewResultData', res.previewResultData);
      Vue.set(asset, '_previewOp', 'done');
      if (res.previewResultData.thumbnail) {
        Vue.set(asset, 'thumbnail', res.previewResultData.thumbnail);
      }

      this.$emit({ asset });
      this.finish();
    },

    onFail() {
      const asset = this.options.asset;

      Vue.set(asset, 'previewResultData', null);
      Vue.set(asset, '_previewOp', 'fail');

      this.$emit({ error: true, asset });
      this.abort();
    },
  },
  {
    isNeeded(asset) {
      const noPRD = _.isEmpty(asset.previewResultData);
      const nonSocialFragment =
        !app.assetType.isDynamicProvider(asset) &&
        app.assetType.isFRAGMENT(asset) &&
        !app.assetType.isSOCIALFRAGMENT(asset);
      const dynamicFragment =
        app.assetType.isDynamicProvider(asset) &&
        app.assetType.isFRAGMENT(asset) &&
        asset.metadata.uploadStatus === 'DONE';
      const isTrimmable = nonSocialFragment || dynamicFragment;
      const running = workPool.has({
        type: this.type,
        context: (jc) => jc.assetId === asset.id,
      });

      return noPRD && isTrimmable && !running;
    },

    execute({ asset }) {
      const isNeeded = this.isNeeded(asset);
      isNeeded && workPool.add(new this({ asset }));
      return isNeeded;
    },

    updateTimelineTrack({ asset, assets }) {
      let needSerialization = false;

      const relevant = _.filter(assets, (visualTrack) => {
        return (
          !visualTrack._realTimePreviewInProgress &&
          visualTrack.id === asset.id &&
          visualTrack.startTime === asset.startTime &&
          visualTrack.endTime === asset.endTime
        );
      });
      _.each(relevant, (visualTrack) => {
        visualTrack.previewResultData = asset.previewResultData;
        visualTrack.thumbnail = visualTrack.previewResultData.thumbnail;

        if (
          !visualTrack.realTimePreviewResultData &&
          !visualTrack._realTimePreviewInProgress
        ) {
          if (app.assetType.isFRAGMENT(visualTrack)) {
            needSerialization = true;
          }
        }
      });

      return needSerialization;
    },

    updateSelectedAsset(asset, selectedAssets) {
      const selectedOne = _.find(selectedAssets, { id: asset.id });
      if (selectedOne && asset !== selectedOne) {
        Vue.$$set(selectedOne, asset);
      }
    },
  }
);
